import React from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useAuth } from '../contexts/AuthContext';
import { SignInButton } from './authComponents';
import { AccessDenied } from '../components/auth/AccessDenied';
import { Users, FileText, Scale } from 'lucide-react';
import { logoConfig } from '../assets/logos';

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * Enhanced AuthGuard component that checks both MSAL authentication and M365 group membership
 * Part of Phase C - M365 Group Authentication Authorization implementation
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const { 
    isAuthorized, 
    isCheckingAuthorization, 
    userPrincipalName, 
    userGroups,
    userRole,
    checkGroupAuthorization,
    signOut 
  } = useAuth();

  // Show loading spinner while authentication is in progress
  if (inProgress === "startup" || inProgress === "handleRedirect") {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src={logoConfig.dark} 
              alt={logoConfig.alt}
              className="h-16 w-auto opacity-75"
            />
          </div>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-300 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated with Microsoft, show sign-in page
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="max-w-md w-full space-y-8 p-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <img 
                src={logoConfig.dark} 
                alt={logoConfig.alt}
                className="h-16 w-auto"
              />
            </div>
            <h2 className="text-xl text-gray-300 mb-8">Legal Practice Management</h2>
          </div>
          
          <div className="bg-gray-800 rounded-lg p-6 space-y-4">
            <div className="flex items-center space-x-3 text-gray-300">
              <Users className="w-5 h-5 text-blue-500" />
              <span>Client Management</span>
            </div>
            <div className="flex items-center space-x-3 text-gray-300">
              <FileText className="w-5 h-5 text-blue-500" />
              <span>Matter Tracking</span>
            </div>
            <div className="flex items-center space-x-3 text-gray-300">
              <Scale className="w-5 h-5 text-blue-500" />
              <span>Document Management</span>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-gray-400 mb-6">
              Sign in with your Microsoft 365 account to access your legal matters and documents.
            </p>
            <SignInButton 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
            />
            <p className="text-xs text-gray-500 mt-4">
              Secure authentication powered by Microsoft 365 • IntelligentHive
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If authenticated but checking authorization, show loading
  if (isCheckingAuthorization || isAuthorized === null) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src={logoConfig.dark} 
              alt={logoConfig.alt}
              className="h-16 w-auto opacity-75"
            />
          </div>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-300 mt-4">Checking authorization...</p>
          {userPrincipalName && (
            <p className="text-gray-500 text-sm mt-2">Signed in as {userPrincipalName}</p>
          )}
          {userRole && (
            <p className="text-gray-400 text-xs mt-1">Role: {userRole}</p>
          )}
        </div>
      </div>
    );
  }

  // If authenticated but not authorized, show access denied
  if (!isAuthorized) {
    return (
      <AccessDenied
        userPrincipalName={userPrincipalName}
        userGroups={userGroups}
        onSignOut={signOut}
        onRetry={checkGroupAuthorization}
      />
    );
  }

  // If authenticated AND authorized, render the protected content
  return <>{children}</>;
};
